import React, { useMemo, useRef, useState } from 'react';
import type { GuestConsentDecisions } from '../../api/donorSessionApi';
import type { DonorPolicyPresentation } from '../../api/voiceInvitationsApi';
import { EmptyState, PillButton } from '../../components/ui';
import {
  approvedDonorPolicies,
  DonorConsentKind,
} from './donorConsentPolicy';

interface DonorConsentProps {
  policies: Record<string, DonorPolicyPresentation>;
  busy: boolean;
  error: boolean;
  onSubmit: (decisions: GuestConsentDecisions) => void;
  onDecline: () => void;
}

/**
 * Four independent, unselected consent choices. Every string here is
 * protected wording; only the presentation is the redesign's.
 */
const DonorConsent: React.FC<DonorConsentProps> = ({
  policies,
  busy,
  error,
  onSubmit,
  onDecline,
}) => {
  const approved = useMemo(() => approvedDonorPolicies(policies), [policies]);
  const [choices, setChoices] = useState<Partial<Record<DonorConsentKind, boolean>>>({});
  const [validationError, setValidationError] = useState(false);
  const firstControlRef = useRef<HTMLInputElement>(null);

  if (!approved) {
    return (
      <div className="donor-step" role="alert">
        <EmptyState
          headingLevel={1}
          title="This invitation needs an updated consent form"
          message="Recording has not started. Ask the person who invited you for a new link."
        />
      </div>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (approved.some((policy) => choices[policy.kind] !== true)) {
      setValidationError(true);
      firstControlRef.current?.focus();
      return;
    }
    setValidationError(false);
    onSubmit({
      voice_model_creation: 'granted',
      household_voice_share: 'granted',
      terms: 'acknowledged',
      privacy: 'acknowledged',
    });
  };

  return (
    <div className="donor-step" aria-busy={busy || undefined}>
      <h1>Choose what you agree to</h1>
      <p>Each choice is required and recorded separately. Nothing is selected for you.</p>
      <form className="donor-consent" onSubmit={submit} noValidate>
        <fieldset
          className="donor-consent__choices"
          disabled={busy}
          aria-describedby={validationError ? 'donor-consent-error' : undefined}
        >
          <legend className="luma-sr-only">Voice recording consent</legend>
          {approved.map((policy, index) => (
            <label className="donor-consent__choice" key={policy.kind}>
              <input
                ref={index === 0 ? firstControlRef : undefined}
                type="checkbox"
                checked={choices[policy.kind] === true}
                onChange={(event) => {
                  setChoices((current) => ({
                    ...current,
                    [policy.kind]: event.target.checked,
                  }));
                  setValidationError(false);
                }}
              />
              <span>{policy.text}</span>
            </label>
          ))}
        </fieldset>
        {validationError && (
          <p id="donor-consent-error" className="donor-error" role="alert">
            Select all four choices to continue.
          </p>
        )}
        {error && (
          <p className="donor-error" role="alert">
            We couldn’t save your choices. Nothing was changed; try again.
          </p>
        )}
        <div className="donor-actions">
          <PillButton variant="primary" type="submit" busy={busy} busyLabel="Saving choices…">
            Agree and continue
          </PillButton>
          <PillButton variant="legal" onClick={onDecline} disabled={busy}>
            Decline invitation
          </PillButton>
        </div>
      </form>
    </div>
  );
};

export default DonorConsent;
